import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, CheckCircle2, ChevronRight, Clock, Loader2, Zap } from 'lucide-react';
import { PodcastEpisode, EpisodeStatus } from '@/types/episode.types';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { connectSocket, onPodcastMetricsUpdate, PodcastMetricsEvent } from '@/services/socket';
import { MobileSummaryCategory } from './MobileContainer';
import { MobileEpisodeCard } from './MobileEpisodeCard';

// Map mobile summary categories to episode statuses
const CATEGORY_STATUS_MAP: Record<MobileSummaryCategory, EpisodeStatus[]> = {
  needsReview: [
    EpisodeStatus.SCRIPTS_PENDING_REVIEW,
    EpisodeStatus.AUDIO_PENDING_REVIEW,
  ],
  ready: [
    EpisodeStatus.MERGED,
    EpisodeStatus.AUDIO_APPROVED,
  ],
  published: [
    EpisodeStatus.PUBLISHED,
  ],
  failed: [
    EpisodeStatus.FAILED,
    EpisodeStatus.SCRIPT_FAILED,
    EpisodeStatus.AUDIO_FAILED,
    EpisodeStatus.PUBLISHING_FAILED,
  ],
};

interface MobileStatusSummaryProps {
  onStatusSelect: (category: MobileSummaryCategory) => void;
  onEpisodeSelect: (episodeId: string) => void;
}

export const MobileStatusSummary: React.FC<MobileStatusSummaryProps> = ({
  onStatusSelect,
  onEpisodeSelect,
}) => {
  const [metrics, setMetrics] = useState<{
    needsReview: number;
    inProgress: number;
    readyToPublish: number;
    published: number;
    failed: number;
  }>({ needsReview: 0, inProgress: 0, readyToPublish: 0, published: 0, failed: 0 });
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [fetchedMetrics, episodesResult] = await Promise.all([
          PodcastEpisodeService.getMetrics(),
          PodcastEpisodeService.getEpisodesPaginated({
            page: 1,
            limit: 50, // Fetch enough to have episodes for each category
            orderBy: 'updatedAt',
            order: 'desc',
          }),
        ]);
        setMetrics(fetchedMetrics);
        setEpisodes(episodesResult.episodes);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Subscribe to real-time metrics updates
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupWebSocket = async () => {
      try {
        await connectSocket();
        cleanup = onPodcastMetricsUpdate((event: PodcastMetricsEvent) => {
          setMetrics(event.metrics);
        });
      } catch (error) {
        console.error('Error setting up WebSocket for metrics:', error);
      }
    };

    setupWebSocket();

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Helper to filter episodes by category
  const getEpisodesForCategory = (category: MobileSummaryCategory): PodcastEpisode[] => {
    const statuses = CATEGORY_STATUS_MAP[category];
    return episodes.filter(ep =>
      statuses.includes(ep.status) ||
      (category === 'failed' && ep.failedClipCount && ep.failedClipCount > 0)
    );
  };

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <div className="flex justify-between items-center mb-6">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Status section configuration
  const statusSections: Array<{
    category: MobileSummaryCategory;
    label: string;
    count: number;
    icon: React.ReactNode;
    colorClass: string;
    bgClass: string;
    pulse?: boolean;
  }> = [
    {
      category: 'needsReview',
      label: 'Needs Review',
      count: metrics.needsReview,
      icon: <Clock className="h-5 w-5" />,
      colorClass: 'text-amber-500',
      bgClass: 'bg-amber-500/5 border-amber-500/20',
    },
    {
      category: 'ready',
      label: 'Ready to Publish',
      count: metrics.readyToPublish,
      icon: <CheckCircle2 className="h-5 w-5" />,
      colorClass: 'text-teal-500',
      bgClass: 'bg-teal-500/5 border-teal-500/20',
    },
    {
      category: 'published',
      label: 'Published',
      count: metrics.published,
      icon: <Zap className="h-5 w-5" />,
      colorClass: 'text-green-500',
      bgClass: 'bg-green-500/5 border-green-500/20',
    },
    {
      category: 'failed',
      label: 'Failed',
      count: metrics.failed,
      icon: <AlertCircle className="h-5 w-5" />,
      colorClass: 'text-red-500',
      bgClass: 'bg-red-500/5 border-red-500/20',
      pulse: true,
    },
  ];

  const MAX_EPISODES_PER_SECTION = 3;

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold">Episode Composer</h1>
          <p className="text-sm text-muted-foreground">Episode overview</p>
        </div>
      </div>

      {/* In Progress Info */}
      {metrics.inProgress > 0 && (
        <Card className="p-3 bg-muted/50">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{metrics.inProgress} episode{metrics.inProgress !== 1 ? 's' : ''} in progress</span>
          </div>
        </Card>
      )}

      {/* Status Sections with Episode Lists */}
      {statusSections.map((section) => {
        const categoryEpisodes = getEpisodesForCategory(section.category);
        const hasEpisodes = categoryEpisodes.length > 0;
        const displayedEpisodes = categoryEpisodes.slice(0, MAX_EPISODES_PER_SECTION);
        const hasMore = categoryEpisodes.length > MAX_EPISODES_PER_SECTION;

        return (
          <div key={section.category} className="space-y-3">
            {/* Section Header */}
            <Card
              className={`p-3 cursor-pointer transition-all active:scale-[0.99] ${section.bgClass} hover:opacity-80`}
              onClick={() => onStatusSelect(section.category)}
            >
              <div className="flex items-center gap-3">
                <div className={section.colorClass}>
                  {section.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{section.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {section.count} episode{section.count !== 1 ? 's' : ''}
                  </p>
                </div>
                {section.pulse && section.count > 0 && (
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </div>
            </Card>

            {/* Episode List */}
            {hasEpisodes && (
              <div className="space-y-2 pl-2">
                {displayedEpisodes.map((episode) => (
                  <MobileEpisodeCard
                    key={episode.id}
                    episode={episode}
                    onClick={() => onEpisodeSelect(episode.id)}
                  />
                ))}
                {hasMore && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground"
                    onClick={() => onStatusSelect(section.category)}
                  >
                    View all {section.count} episodes
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
